import { Controller } from 'stimulus'

const CHIPS_SHOWN = 12

// The selection persists across searches (chips) and posts as a comma-joined client_ids field.
export default class extends Controller {
  static targets = ['search', 'category', 'results', 'chips', 'hidden', 'count', 'submit']

  connect() {
    this.selected = new Map() // id -> name
    this.listed = [] // the bookless clients the current search shows
    this._debounce = null
    this._seq = 0 // guards against out-of-order fetch responses clobbering results
    // Autocomplete: nothing is shown until the user searches (or picks a category).
  }

  onInput() {
    clearTimeout(this._debounce)
    this._debounce = setTimeout(() => this.fetch(), 250)
  }

  fetch() {
    const q   = this.hasSearchTarget ? this.searchTarget.value.trim() : ''
    const cat = this.hasCategoryTarget ? this.categoryTarget.value : ''
    if (!q && !cat) { this._hideResults(); return } // autocomplete: no query → no list
    const seq = ++this._seq
    const url = new URL(this.data.get('url'), window.location.origin)
    if (q) url.searchParams.set('q', q)
    if (cat) url.searchParams.set('category_id', cat)
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => { if (seq === this._seq) this._renderResults(d) })
      .catch(() => {})
  }

  _renderResults(d) {
    // Select all takes the clients with no book only: one already filed is moved one by one, knowingly.
    this.listed = (d.clients || []).filter((c) => !c.portfolio).map((c) => ({ id: c.id, name: c.name }))
    const rows = (d.clients || []).map((c) => {
      const on = this.selected.has(c.id)
      return `<button type="button" data-action="crm-client-picker#toggle" data-id="${c.id}" data-name="${this._esc(c.name)}"
        class="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[13px] hover:bg-slate-50 ${on ? 'bg-blue-50' : ''}">
        <span class="truncate">${this._esc(c.name)}${c.category ? `<span class="text-slate-400"> · ${this._esc(c.category)}</span>` : ''}${
          c.portfolio ? `<span class="text-amber-600"> · ${this._esc(I18n.t('crm.portfolios.add.in_book', { name: c.portfolio }))}</span>` : ''}</span>
        <span class="text-[12px] ${on ? 'text-blue-600' : 'text-slate-300'}">${on ? '✓' : '+'}</span>
      </button>`
    }).join('')
    const more = d.total > (d.clients || []).length
      ? `<div class="text-[11px] text-slate-400 px-3 py-2">${I18n.t('crm.portfolios.add.too_many', { count: d.total })}</div>`
      : ''
    this.resultsTarget.innerHTML = (rows && this._selectAllRow() + rows + more) ||
      `<div class="text-[12.5px] text-slate-400 px-3 py-4">${I18n.t('crm.portfolios.add.none')}</div>`
    this._showResults()
  }

  _selectAllRow() {
    if (this.listed.length === 0) return ''
    const all = this.listed.every((c) => this.selected.has(c.id))
    const label = all ? I18n.t('crm.portfolios.add.unselect_all') : I18n.t('crm.portfolios.add.select_all', { count: this.listed.length })
    return `<button type="button" data-action="crm-client-picker#toggleAll" data-target="crm-client-picker.selectAll"
      class="w-full px-3 py-2 text-left text-[12.5px] font-medium text-blue-600 hover:bg-slate-50">${this._esc(label)}</button>`
  }

  toggleAll() {
    const all = this.listed.every((c) => this.selected.has(c.id))
    this.listed.forEach((c) => (all ? this.selected.delete(c.id) : this.selected.set(c.id, c.name)))
    this._sync()
    this.resultsTarget.querySelectorAll('[data-action="crm-client-picker#toggle"]').forEach((btn) => this._mark(btn))
  }

  _mark(btn) {
    const on = this.selected.has(parseInt(btn.dataset.id, 10))
    const mark = btn.querySelector('span:last-child')
    btn.classList.toggle('bg-blue-50', on)
    mark.textContent = on ? '✓' : '+'
    mark.className = `text-[12px] ${on ? 'text-blue-600' : 'text-slate-300'}`
  }

  _refreshSelectAll() {
    const row = this.hasResultsTarget && this.resultsTarget.querySelector('[data-target="crm-client-picker.selectAll"]')
    if (row) row.outerHTML = this._selectAllRow()
  }

  _showResults() { if (this.hasResultsTarget) this.resultsTarget.classList.remove('hidden') }
  _hideResults() { if (this.hasResultsTarget) { this.resultsTarget.classList.add('hidden'); this.resultsTarget.innerHTML = '' } }

  toggle(e) {
    const btn = e.currentTarget
    const id = parseInt(btn.dataset.id, 10)
    if (this.selected.has(id)) this.selected.delete(id)
    else this.selected.set(id, btn.dataset.name)
    this._sync()
    this._mark(btn)
  }

  removeChip(e) {
    const id = parseInt(e.currentTarget.dataset.id, 10)
    this.selected.delete(id)
    this._sync()
    const row = this.hasResultsTarget && this.resultsTarget.querySelector(`[data-id="${id}"]`)
    if (row) this._mark(row)
  }

  _sync() {
    const ids = [...this.selected.keys()]
    this.hiddenTarget.value = ids.join(',')
    if (this.hasCountTarget) this.countTarget.textContent = ids.length
    if (this.hasSubmitTarget) this.submitTarget.disabled = ids.length === 0
    if (this.listed) this._refreshSelectAll()
    if (this.hasChipsTarget) {
      const entries = [...this.selected.entries()]
      const hidden  = entries.length - CHIPS_SHOWN
      this.chipsTarget.innerHTML = entries.slice(0, CHIPS_SHOWN).map(([id, name]) =>
        `<span class="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full pl-2.5 pr-1 py-0.5 text-[12px]">
          ${this._esc(name)}
          <button type="button" data-action="crm-client-picker#removeChip" data-id="${id}" class="w-4 h-4 grid place-items-center rounded-full hover:bg-blue-100">✕</button>
        </span>`).join('') +
        (hidden > 0 ? `<span class="text-[12px] text-slate-500 py-0.5">${I18n.t('crm.portfolios.add.more_selected', { count: hidden })}</span>` : '')
    }
  }

  _esc(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML }
}
